import { CommonModule } from "@angular/common";
import { Component, OnDestroy, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { IonContent, IonicModule } from "@ionic/angular";
import { Observable, Subject } from "rxjs";
import { BehaviorSubject } from "rxjs";
import { forkJoin } from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";

//Services
import { EventsService } from "@providers/EventsService";
import { DeviceService } from "@services/device.service";
import { MobileBehaviorService, SportBookService, SportService, TicketService } from "@sports-services/index";
import { SportNavigationService } from "@sports-services/index";

//Models
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { C_GameTypes, SixPack } from "@sports-models/index";
import { C_LeagueCategory } from "@sports-models/index";
import { C_Event } from "@sports-models/index";

// Components
import { CodereNavbar } from "@components/common/codere-navbar/codere-navbar";
import { CodereFooterComponent } from "@components/mso/footer/codere-footer/codere-footer";
import {
  SbButtonComponent,
  SbCounterComponent,
  SbGridContentExpandedComponent,
  SbGridContentSingleComponent,
  SbGridContentTeamsComponent,
  SbGridHeaderComponent,
  SbGridItemComponent,
  SbHeaderWithCounterComponent,
  SbOverviewScoreRegularComponent,
  SbOverviewScoreSetsComponent,
  SbSimpleNavbarComponent,
} from "@sports-components/index";

// pipes & directives
import { SportsBetDisplayDirective } from "@sports-directives/bet-display/bet-display";
import { SportsExtractDatePipe } from "@sports-pipes/sports.extract-date.pipe";

//Others
import { SPORTS_PATHS } from "@shared-constants/routes";
import { SportsBasePage, TrackingInfo } from "@sports-pages/index";

//store
import { Store } from "@ngrx/store";
import { NewTicketFacade } from "@sports-features/ticket/stores";
import { TicketItemTypesEnum } from "@sports-pages/ticket/utils/ticket.constants";
import { SportsbookState } from "@sports-stores/sportsbook/sportsbook.reducers";
import * as sportsbookSelector from "@sports-stores/sportsbook/sportsbook.selectors";
import { SCOREBOARD_TYPE } from "@sports-utils/sports.constants";

@Component({
  selector: "page-evento-special",
  templateUrl: "./evento-special.page.html",
  standalone: true,
  imports: [
    IonicModule,
    FormsModule,
    CommonModule,
    SbSimpleNavbarComponent,
    SbGridItemComponent,
    SbOverviewScoreSetsComponent,
    SbOverviewScoreRegularComponent,
    SbButtonComponent,
    CodereFooterComponent,
    SbGridContentTeamsComponent,
    SbGridContentExpandedComponent,
    SportsExtractDatePipe,
    SportsBetDisplayDirective,
    CodereNavbar,
    SbGridContentSingleComponent,
    SbGridHeaderComponent,
    SbHeaderWithCounterComponent,
    SbCounterComponent,
  ],
})
export class EventoSpecialPage extends SportsBasePage implements OnDestroy {
  @ViewChild(IonContent) content: IonContent;
  event: any;
  leagues: string[];
  categories$: Observable<any[]>;
  currentCategory$: BehaviorSubject<C_LeagueCategory> = new BehaviorSubject(null);
  events$: Observable<any[]>;
  auxEvents$: Observable<any>;
  polling$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  hasSixPack: boolean;

  globalVars: GlobalVarsModel;
  router = inject(Router);
  sportService = inject(SportService);

  sportBookServ = inject(SportBookService);
  ticketService = inject(TicketService);
  sportBookService = inject(SportBookService);
  newTicketFacade = inject(NewTicketFacade);
  mobileBehavior = inject(MobileBehaviorService);
  extractDate = inject(SportsExtractDatePipe);
  eventService = inject(EventsService);
  deviceService = inject(DeviceService);
  sportsbookStore = inject(Store<SportsbookState>);
  sportNavigationService = inject(SportNavigationService);

  isMobile = false;
  isDesktop = false;
  gameTypes: C_GameTypes;

  private ngUnsubscribe$ = new Subject<boolean>();

  SCOREBOARD_TYPE = SCOREBOARD_TYPE;

  constructor() {
    super();
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();
    const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;

    this.event = currentUrl.state.params;
    this.event.idLeagues = this.event.idLeagues ? this.event.idLeagues.split("-") : [];
    this.hasSixPack = SixPack.validate(this.event.SportHandle, this.event.CountryCode);
    this.sportsbookStore
      .select(sportsbookSelector.getGameTypeConfig)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(({ gameTypes }) => {
        this.gameTypes = new C_GameTypes(gameTypes);
      });
  }

  getTrackingInfo(): TrackingInfo {
    return {
      uri: encodeURI(`/specialEvent/${this.event.Name}`),
      description: `Evento especial page - ${this.event.Name}`,
      additionalData: {
        nodeId: this.event.NodeId,
        nodeName: this.event.Name,
        sportHandle: this.event.SportHandle,
      },
    };
  }

  ngOnInit() {
    this.getCategories();
    this.events$ = this.currentCategory$.pipe(
      switchMap((category) => {
        if (!category) {
          category = new C_LeagueCategory(-1, "", false);
        }
        return this.getEvents(category.CategoryId);
      }),
    );
    this.getAuxEvents();
  }

  public changeCategory(value: any): void {
    this.currentCategory$.next(value);
    this.content.scrollToTop();
  }

  public navigate(market, gameType?): void {
    const component: string = market.isLive ? SPORTS_PATHS.NowLiveDetailPage : SPORTS_PATHS.MercadosPage;
    const eventType: string = market.isLive ? "pushEventLive" : "pushEvent";
    this.router.navigate([component], { state: { params: market } });
    this.trackingService.trackEvent([eventType, market.NodeId, window.location.hash, market.Name, "event"]);
  }

  public bet(game: any, event: any, gameName: string, type: string) {
    const data = {
      ...game,
      EventDate: this.extractDate.transform(event.StartDate),
      EventName: event.Name,
      GameName: gameName,
    };
    this.newTicketFacade.checkSelection(data, data.NodeId, TicketItemTypesEnum.LINE_ITEM);
  }

  private getAuxEvents() {
    const requests$ = [];
    this.event.idLeagues.forEach((el, i) => {
      if (!i) {
        return;
      }
      const params = {
        parentId: el,
        sportHandle: this.event.SportHandle,
      };
      requests$.push(this.sportService.getEvents(params));
    });

    this.auxEvents$ = forkJoin(...requests$).pipe(
      map((res) => res.reduce((acc: any[], el: any[]) => [...acc, ...el], [])),
    );
  }

  private getCategories(): void {
    const params: any = {
      idLeague: this.event.idLeagues[0],
      sportHandle: this.event.SportHandle,
      countryCode: this.event.CountryCode,
    };
    this.categories$ = this.sportService.GetCategoriesByLeague(params).pipe(
      map((categories) => {
        let current = this.currentCategory$.getValue();
        categories = Array.isArray(categories) ? categories : [];
        categories = categories.map(
          (cat) => new C_LeagueCategory(cat.CategoryId, cat.CategoryName, cat.IsRelevant),
        );
        const gameTypes = this.gameTypes.getSport(this.event.SportHandle);

        if (this.hasSixPack) {
          categories.unshift(new C_LeagueCategory(-1, "Principal", false));
          categories.filter((category) => gameTypes.indexOf(parseInt(category.CategoryId)) === -1);
        }

        if (this.isDesktop && !this.hasSixPack) {
          categories.unshift(new C_LeagueCategory(-1, "Principal", false));
        }

        if (!current && categories.length > 0) {
          current = categories[0];
          this.currentCategory$.next(current);
        }

        setTimeout(() => this.content.scrollToTop(), 1);
        return categories;
      }),
    );
  }

  private getEvents(category?: any): Observable<C_Event[]> {
    const params = {
      parentId: this.event.idLeagues[0],
      category,
      sportHandle: this.event.SportHandle,
    };

    const request$ = this.sportService.getEvents(params);

    return request$;
  }
  onScroll(e) {
    this.mobileBehavior.scrolling$.next(true);
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next(true);
    this.ngUnsubscribe$.complete();
  }
}
